import React from "react";
import { Camera, Printer, DownloadSimple, MagnifyingGlassPlus } from "@phosphor-icons/react";
import { formatIdDateTime } from "@/lib/format";

// Upload + grid gallery for "Bukti Muat" photos. All state lives with the
// parent so the upload progress bar + lightbox coordinate cleanly.
export default function BuktiMuatPanel({
  proofs, proofBlobs, uploading, uploadPhase, uploadPct,
  onUploadProof, onOpenLightbox, onPrintProof, onDownloadProof,
}) {
  return (
    <div className="border border-border p-6 bg-card mb-8 no-print">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <div>
          <div className="overline">Bukti Muat</div>
          <h3 className="font-bold text-lg tracking-tight">Foto Loading</h3>
        </div>
        <label className={uploading ? "cursor-not-allowed" : "cursor-pointer"}>
          <input
            type="file"
            accept="image/*"
            onChange={onUploadProof}
            className="hidden"
            disabled={uploading}
            data-testid="upload-proof-input"
          />
          <span
            className={`relative overflow-hidden inline-flex items-center gap-2 px-4 h-11 border font-bold uppercase tracking-wider text-sm ${
              uploading ? "bg-secondary" : "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
            }`}
          >
            {uploading && uploadPhase === "upload" && (
              <span
                className="absolute inset-y-0 left-0 bg-primary/25 transition-[width] duration-200 ease-out"
                style={{ width: `${uploadPct}%` }}
                data-testid="upload-proof-progress-bar"
                aria-hidden="true"
              />
            )}
            <Camera size={16} weight="bold" className="relative z-10" />
            <span
              className="relative z-10"
              data-testid={uploading ? "upload-proof-progress-label" : undefined}
            >
              {!uploading && "Upload / Kamera / Galeri"}
              {uploading && uploadPhase === "compress" && "Mengompres foto..."}
              {uploading && uploadPhase === "upload" && `Mengupload ${uploadPct}%`}
            </span>
          </span>
        </label>
      </div>

      {proofs.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center">
          Belum ada bukti muat.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {proofs.map((p) => (
            <div
              key={p.id}
              className="border border-border bg-secondary/30 overflow-hidden"
              data-testid={`proof-card-${p.id}`}
            >
              <div className="relative group">
                {proofBlobs[p.id] ? (
                  <button
                    type="button"
                    onClick={() => onOpenLightbox({ blob: proofBlobs[p.id], proof: p })}
                    className="w-full block cursor-zoom-in"
                    data-testid={`proof-zoom-${p.id}`}
                    aria-label="Perbesar foto bukti muat"
                  >
                    <img src={proofBlobs[p.id]} alt="proof" className="w-full h-40 object-cover" />
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center">
                      <MagnifyingGlassPlus
                        size={28}
                        className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg"
                        weight="bold"
                      />
                    </div>
                  </button>
                ) : (
                  <div className="w-full h-40 bg-secondary animate-pulse" />
                )}
              </div>
              <div className="p-2 text-xs font-mono">
                <div className="truncate">{p.uploaded_by_name}</div>
                <div className="text-muted-foreground">{formatIdDateTime(p.created_at)}</div>
                {proofBlobs[p.id] && (
                  <div className="flex gap-1 mt-2 no-print">
                    <button
                      type="button"
                      onClick={() => onPrintProof(p)}
                      className="flex-1 border border-border bg-background hover:bg-secondary text-[10px] uppercase tracking-wider py-1.5 flex items-center justify-center gap-1"
                      title="Cetak ulang bukti muat"
                      data-testid={`proof-print-${p.id}`}
                    >
                      <Printer size={11} />
                      Cetak
                    </button>
                    <button
                      type="button"
                      onClick={() => onDownloadProof(p)}
                      className="flex-1 border border-border bg-background hover:bg-secondary text-[10px] uppercase tracking-wider py-1.5 flex items-center justify-center gap-1"
                      title="Download foto"
                      data-testid={`proof-download-${p.id}`}
                    >
                      <DownloadSimple size={11} />
                      Unduh
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
